import { TID } from '@atproto/common-web'
import { api } from '../api/client'

/**
 * Own-account automation. These hit the user's PDS as the logged-in
 * session — i.e. you doing things to your own account at sane rates.
 * Multi-account orchestration is intentionally not exposed here; see
 * lib/multiAccountActor.ts for the scaffold.
 */

export type ActionKind = 'like' | 'repost' | 'follow'

export type ActionTarget =
  | { kind: 'post'; uri: string; cid: string }
  | { kind: 'profile'; did: string }

export type ActorSession = {
  service: string
  did: string
  handle: string
  accessJwt: string
  refreshJwt: string
}

export type ActorContext = {
  /** Logged-in session (read from useAuth) */
  session: ActorSession
  /**
   * Spacing between writes. Either a fixed number of ms (legacy / single
   * value) or a [min, max] range — each pause picks a uniform random ms
   * inside [min, max] so the pattern doesn't look like a fixed-interval
   * bot. Keep generous to stay under PDS limits.
   */
  spacingMs: number | { min: number; max: number }
  signal?: AbortSignal
}

function pickSpacingMs(spec: number | { min: number; max: number }): number {
  if (typeof spec === 'number') return Math.max(0, spec)
  const lo = Math.max(0, Math.min(spec.min, spec.max))
  const hi = Math.max(0, Math.max(spec.min, spec.max))
  if (hi <= lo) return lo
  return lo + Math.floor(Math.random() * (hi - lo + 1))
}

/** Sleep that resolves early if the signal aborts (no leftover timeouts). */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve()
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve()
      return
    }
    const t = setTimeout(() => {
      if (signal) signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(t)
      resolve()
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

async function pdsFetch(
  ctx: ActorContext,
  path: string,
  init: RequestInit,
): Promise<Response> {
  const url = `${ctx.session.service}${path}`
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${ctx.session.accessJwt}`,
      ...(init.headers ?? {}),
    },
  })
  return res
}

function nextRkey(): string {
  return TID.nextStr()
}

async function createRecord(
  ctx: ActorContext,
  collection: string,
  record: Record<string, unknown>,
): Promise<{ uri: string; cid: string }> {
  const rkey = nextRkey()
  const body = {
    repo: ctx.session.did,
    collection,
    rkey,
    record,
  }
  const r = await pdsFetch(ctx, '/xrpc/com.atproto.repo.createRecord', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  if (!r.ok) {
    const text = await r.text()
    throw new Error(`createRecord failed (${r.status}): ${text}`)
  }
  return (await r.json()) as { uri: string; cid: string }
}

export async function likePost(
  ctx: ActorContext,
  target: { uri: string; cid: string },
): Promise<void> {
  await createRecord(ctx, 'app.bsky.feed.like', {
    $type: 'app.bsky.feed.like',
    subject: { uri: target.uri, cid: target.cid },
    createdAt: new Date().toISOString(),
  })
}

export async function repostPost(
  ctx: ActorContext,
  target: { uri: string; cid: string },
): Promise<void> {
  await createRecord(ctx, 'app.bsky.feed.repost', {
    $type: 'app.bsky.feed.repost',
    subject: { uri: target.uri, cid: target.cid },
    createdAt: new Date().toISOString(),
  })
}

export async function followAccount(ctx: ActorContext, did: string): Promise<void> {
  await createRecord(ctx, 'app.bsky.graph.follow', {
    $type: 'app.bsky.graph.follow',
    subject: did,
    createdAt: new Date().toISOString(),
  })
}

export type RunOptions = {
  ctx: ActorContext
  action: ActionKind
  targets: ActionTarget[]
  /** Called for each completed (or failed) target */
  onProgress?: (i: number, total: number, error: string | null) => void
}

export async function runActions(opts: RunOptions): Promise<{ ok: number; err: number }> {
  let ok = 0
  let err = 0
  for (let i = 0; i < opts.targets.length; i++) {
    if (opts.ctx.signal?.aborted) break
    const t = opts.targets[i]
    try {
      if (opts.action === 'like' && t.kind === 'post') await likePost(opts.ctx, t)
      else if (opts.action === 'repost' && t.kind === 'post') await repostPost(opts.ctx, t)
      else if (opts.action === 'follow' && t.kind === 'profile')
        await followAccount(opts.ctx, t.did)
      else throw new Error(`mismatched action/target: ${opts.action} vs ${t.kind}`)
      ok += 1
      opts.onProgress?.(i + 1, opts.targets.length, null)
    } catch (e) {
      err += 1
      opts.onProgress?.(i + 1, opts.targets.length, e instanceof Error ? e.message : String(e))
    }
    if (i < opts.targets.length - 1) {
      await sleep(pickSpacingMs(opts.ctx.spacingMs), opts.ctx.signal)
    }
  }
  return { ok, err }
}

export type ParallelRunOptions = {
  sessions: ActorSession[]
  action: ActionKind
  targets: ActionTarget[]
  spacingMs: number | { min: number; max: number }
  signal?: AbortSignal
  /** Per-session progress callback. (sessionDid, done, total, lastError). */
  onProgress?: (sessionDid: string, done: number, total: number, error: string | null) => void
}

/**
 * Run the same target list in parallel from multiple sessions. Each
 * session gets its own runActions loop with independent random spacing
 * — so 5 accounts liking the same 50 posts will scatter their writes
 * naturally rather than firing in lockstep.
 *
 * Returns per-session counts keyed by DID.
 */
export async function runActionsParallel(
  opts: ParallelRunOptions,
): Promise<Record<string, { ok: number; err: number }>> {
  const out: Record<string, { ok: number; err: number }> = {}
  await Promise.all(
    opts.sessions.map(async (session) => {
      const result = await runActions({
        ctx: { session, spacingMs: opts.spacingMs, signal: opts.signal },
        action: opts.action,
        targets: opts.targets,
        onProgress: (done, total, err) => opts.onProgress?.(session.did, done, total, err),
      })
      out[session.did] = result
    }),
  )
  return out
}

/** List the most recent N post URIs+CIDs by the target handle. */
export async function recentPostsOf(
  handle: string,
  service: string,
  limit: number,
  accessJwt?: string,
): Promise<{ uri: string; cid: string }[]> {
  const out: { uri: string; cid: string }[] = []
  let cursor: string | undefined = undefined
  while (out.length < limit) {
    const page = await api.page({
      handle,
      collection: 'app.bsky.feed.post',
      cursor,
      limit: Math.min(100, limit - out.length),
      service,
      accessJwt,
    })
    for (const r of page.records) {
      if (r.cid) out.push({ uri: r.uri, cid: r.cid })
      if (out.length >= limit) break
    }
    if (!page.cursor) break
    cursor = page.cursor
  }
  return out.slice(0, limit)
}

export async function resolveDid(handle: string, service: string): Promise<string | null> {
  try {
    const profile = await api.profile(handle, service)
    return profile.did
  } catch {
    return null
  }
}
